/**
 * Tour Request Agent: Custom hooks for tour request management.
 * Handles tour scheduling, availability, and request management.
 */

import { useState, useCallback } from 'react';
import APIAgent from './apiAgent';
import asArray from '../utils/asArray';

/**
 * Hook for managing tour requests
 */
export const useTourRequests = (buyerId) => {
  const [tours, setTours] = useState([]);
  const [currentTour, setCurrentTour] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch buyer's tour requests
   */
  const fetchTours = useCallback(async () => {
    if (!buyerId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await APIAgent.request(
        `/api/tour-requests?buyer_id=${buyerId}`,
        'GET'
      );
      setTours(asArray(data));
    } catch (err) {
      console.error('Error fetching tour requests:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [buyerId]);

  /**
   * Create a new tour request
   */
  const createTourRequest = useCallback(
    async (houseId, message = '', preferredDates = [], duration = 30) => {
      try {
        setLoading(true);
        setError(null);
        const data = await APIAgent.request('/api/tour-requests', 'POST', {
          buyer_id: buyerId,
          house_id: houseId,
          message,
          preferred_dates_json: preferredDates,
          scheduled_duration_minutes: duration,
        });
        setTours((prev) => [...prev, data]);
        return data;
      } catch (err) {
        console.error('Error creating tour request:', err);
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [buyerId]
  );

  /**
   * Get tour request details
   */
  const getTourRequest = useCallback(async (tourId) => {
    try {
      setLoading(true);
      setError(null);
      const data = await APIAgent.request(
        `/api/tour-requests/${tourId}`,
        'GET'
      );
      setCurrentTour(data);
      return data;
    } catch (err) {
      console.error('Error getting tour request:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Schedule a tour
   */
  const scheduleTour = useCallback(async (tourId, scheduledDate, duration = 30, agentId = null) => {
    try {
      setError(null);
      const data = await APIAgent.request(
        `/api/tour-requests/${tourId}/schedule`,
        'PUT',
        {
          scheduled_date: scheduledDate,
          scheduled_duration_minutes: duration,
          agent_id: agentId,
        }
      );
      setCurrentTour(data);
      setTours((prev) =>
        prev.map((t) => (t.id === tourId ? data : t))
      );
      return data;
    } catch (err) {
      console.error('Error scheduling tour:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Cancel a tour request
   */
  const cancelTour = useCallback(async (tourId, reason = '') => {
    try {
      setError(null);
      const url = reason
        ? `/api/tour-requests/${tourId}/cancel?reason=${encodeURIComponent(reason)}`
        : `/api/tour-requests/${tourId}/cancel`;
      const data = await APIAgent.request(url, 'PUT');
      setCurrentTour(data);
      setTours((prev) =>
        prev.map((t) => (t.id === tourId ? data : t))
      );
      return data;
    } catch (err) {
      console.error('Error cancelling tour:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Complete a tour
   */
  const completeTour = useCallback(async (tourId) => {
    try {
      setError(null);
      const data = await APIAgent.request(
        `/api/tour-requests/${tourId}/complete`,
        'POST'
      );
      setCurrentTour(data);
      setTours((prev) =>
        prev.map((t) => (t.id === tourId ? data : t))
      );
      return data;
    } catch (err) {
      console.error('Error completing tour:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Update tour request
   */
  const updateTourRequest = useCallback(async (tourId, updates) => {
    try {
      setError(null);
      const data = await APIAgent.request(
        `/api/tour-requests/${tourId}`,
        'PUT',
        updates
      );
      setCurrentTour(data);
      setTours((prev) =>
        prev.map((t) => (t.id === tourId ? data : t))
      );
      return data;
    } catch (err) {
      console.error('Error updating tour request:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Delete a tour request
   */
  const deleteTour = useCallback(async (tourId) => {
    try {
      setError(null);
      await APIAgent.request(`/api/tour-requests/${tourId}`, 'DELETE');
      setTours((prev) => prev.filter((t) => t.id !== tourId));
      if (currentTour?.id === tourId) {
        setCurrentTour(null);
      }
    } catch (err) {
      console.error('Error deleting tour request:', err);
      setError(err.message);
      throw err;
    }
  }, [currentTour?.id]);

  /**
   * Submit buyer feedback after a completed tour
   */
  const submitTourFeedback = useCallback(async (tourId, rating, feedback = '') => {
    try {
      setError(null);
      const data = await APIAgent.request(
        `/api/tour-requests/${tourId}/feedback`,
        'POST',
        { buyer_rating: rating, buyer_feedback: feedback || undefined }
      );
      setTours((prev) => prev.map((t) => (t.id === tourId ? data : t)));
      return data;
    } catch (err) {
      console.error('Error submitting tour feedback:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  return {
    tours,
    currentTour,
    loading,
    error,
    fetchTours,
    createTourRequest,
    getTourRequest,
    scheduleTour,
    cancelTour,
    completeTour,
    updateTourRequest,
    deleteTour,
    submitTourFeedback,
  };
};

/**
 * Hook for checking tour availability
 */
export const useTourAvailability = () => {
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Get available tour slots for a property
   */
  const getAvailability = useCallback(async (houseId, startDate, endDate = null, duration = 30) => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        start_date: startDate.toISOString(),
        duration_minutes: duration,
      });
      
      if (endDate) {
        params.append('end_date', endDate.toISOString());
      }

      const data = await APIAgent.request(
        `/api/tour-requests/houses/${houseId}/tour-availability?${params.toString()}`,
        'GET'
      );
      setAvailability(data);
      return data;
    } catch (err) {
      console.error('Error getting tour availability:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    availability,
    loading,
    error,
    getAvailability,
  };
};

/**
 * Hook for seller tour request management
 */
export const useSellerTourRequests = (sellerId) => {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch all tour requests for the seller's properties
   */
  const fetchSellerTours = useCallback(async () => {
    if (!sellerId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await APIAgent.request(
        `/api/tour-requests?seller_id=${sellerId}`,
        'GET'
      );
      setTours(asArray(data));
    } catch (err) {
      console.error('Error fetching seller tour requests:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [sellerId]);

  return {
    tours,
    loading,
    error,
    fetchSellerTours,
  };
};

/**
 * Hook for managing seller availability windows for a property.
 * Fetches, saves, and mutates recurring_windows and blocked_dates per house.
 */
export const useSellerAvailability = (houseId) => {
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAvailability = useCallback(async () => {
    if (!houseId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await APIAgent.request(
        `/api/houses/${houseId}/availability`,
        'GET'
      );
      setAvailability(data);
      return data;
    } catch (err) {
      if (err?.status === 404 || err?.message?.includes('404')) {
        setAvailability(null);
      } else {
        console.error('Error fetching seller availability:', err);
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [houseId]);

  const upsertAvailability = useCallback(async (data, sellerId) => {
    try {
      setLoading(true);
      setError(null);
      const result = await APIAgent.request(
        `/api/houses/${houseId}/availability?seller_id=${sellerId}`,
        'PUT',
        data
      );
      setAvailability(result);
      return result;
    } catch (err) {
      console.error('Error saving seller availability:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [houseId]);

  const addBlockedDate = useCallback(async (date, note) => {
    try {
      setLoading(true);
      setError(null);
      const result = await APIAgent.request(
        `/api/houses/${houseId}/availability/blocked-dates`,
        'POST',
        { date, ...(note ? { note } : {}) }
      );
      setAvailability(result);
      return result;
    } catch (err) {
      console.error('Error adding blocked date:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [houseId]);

  const removeBlockedDate = useCallback(async (date) => {
    try {
      setLoading(true);
      setError(null);
      const result = await APIAgent.request(
        `/api/houses/${houseId}/availability/blocked-dates/${date}`,
        'DELETE'
      );
      setAvailability(result);
      return result;
    } catch (err) {
      console.error('Error removing blocked date:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [houseId]);

  return {
    availability,
    loading,
    error,
    fetchAvailability,
    upsertAvailability,
    addBlockedDate,
    removeBlockedDate,
  };
};
